/**
 * Библиотеки референсов: типы ответов `/api/libraries*` и вызовы записи.
 *
 * Библиотека — то, на что мы опираемся (интерьеры, действия, образы), а не то,
 * что снимаем: референс переживает проект, поэтому живёт отдельно от него.
 */
import { apiFetch, query } from "@/lib/api";
import type { TagBy, TagLite } from "@/lib/tags";
import type { Reference } from "@/lib/project";
import type { SimilarResult } from "@/lib/similar";
import type { SiteLevel, SitesData } from "@/lib/sites";

export type LibraryCard = {
  id: number;
  name: string;
  note: string | null;
  category_code: string;
  category_name: string;
  photos: number;
  cover: string | null;
};

export type LibraryGroup = { code: string; name: string; libs: LibraryCard[] };

export type Category = { id: number; code: string; name: string };

export type LibraryList = { groups: LibraryGroup[]; categories: Category[]; items_total: number };

export type Library = {
  id: number;
  name: string;
  note: string | null;
  category_code: string;
  category_name: string;
  photos: number;
  used: number;
  generations: number;
};

export type Verdict = "works" | "fails";

/** Строка референса — одна на библиотеку, «Все референсы», корзину и проект. */
export type ReferenceItem = {
  id: number;
  library_id: number;
  library_name: string;
  category_code: string;
  category_name: string;
  asset_id: number;
  asset_path: string;
  asset_width: number | null;
  asset_height: number | null;
  note: string | null;
  verdict: Verdict | null;
  tag_ids: number[];
  /** Из `tag_ids` — метки, которые поставил только автотег. */
  auto_tag_ids: number[];
  used: number;
  generations: number;
  in_basket: boolean;
  date_create: string;
  /** Только в корзине: когда положили. */
  date_add?: string;
};

export type LibraryDetail = { library: Library; items: ReferenceItem[] };

export type UploadResult = { added: number; errors: string[] };

/** Категория новой библиотеки по умолчанию из трендов: пины — это образы и фасоны. */
export const LOOK_CATEGORY = "look";

export const libraryKeys = {
  list: ["libraries"] as const,
  one: (id: number) => ["library", id] as const,
  all: (params: AllItemsParams) => ["library", "all", params] as const,
  basket: ["library", "basket"] as const,
  /** Под `library`: перенос и удаление фото перезапрашивают и площадки. */
  sites: (id: number, level: SiteLevel) => ["library", "sites", id, level] as const,
  /** Под `library`: удаление и перенос перезапрашивают и выдачу поиска. */
  similar: (source: string, libraryId: number | null) => ["library", "similar", source, libraryId] as const,
};

export type AllItemsParams = {
  category?: string;
  library_id?: number;
  tag?: number | "none";
  verdict?: Verdict | "none";
  q?: string;
};
export type AllItems = { items: ReferenceItem[]; total: number; truncated: boolean; max: number };
export type BasketView = { items: ReferenceItem[]; max: number };

/** Потолок корзины на сервере (`basket.MAX`): сверх него добавление — 409. */
export const BASKET_MAX = 200;

export const verdictLabel: Record<Verdict, string> = { works: "работает", fails: "не работает" };

export const loadLibraries = () => apiFetch<LibraryList>("/api/libraries");
export const loadLibrary = (id: number) => apiFetch<LibraryDetail>(`/api/libraries/${id}`);
export const loadSites = (libraryId: number, level: SiteLevel) =>
  apiFetch<SitesData>(`/api/library/${libraryId}/sites${query({ level })}`);

export type NewLibrary = {
  name: string;
  /** Код существующей категории; пустой — заводим новую по `category_name`. */
  category: string;
  category_name: string;
  note: string;
};

export const createLibrary = (body: NewLibrary) =>
  apiFetch<{ id: number }>("/api/libraries", { method: "POST", body: JSON.stringify(body) });

export function uploadPhotos(libraryId: number, files: File[]): Promise<UploadResult> {
  const form = new FormData();
  for (const file of files) form.append("files", file, file.name || "paste.png");
  return apiFetch<UploadResult>(`/api/libraries/${libraryId}/upload`, { method: "POST", body: form });
}

/** Похожие референсы: по картинке, по платью «Наших фото» ИЛИ по описанию; `libraryId` сужает поиск. */
export function searchSimilar(q: { file?: File; dressId?: number; text?: string; libraryId?: number; photoIds?: number[] }): Promise<SimilarResult> {
  const form = new FormData();
  if (q.file) form.append("file", q.file, q.file.name || "paste.png");
  if (q.dressId !== undefined) form.append("dress_id", String(q.dressId));
  if (q.text !== undefined) form.append("text", q.text);
  if (q.photoIds?.length) form.append("photo_ids", q.photoIds.join(","));
  if (q.libraryId !== undefined) form.append("library_id", String(q.libraryId));
  return apiFetch<SimilarResult>("/api/library/similar", { method: "POST", body: form });
}

export const saveItemNote = (itemId: number, note: string) =>
  apiFetch<{ value: string }>(`/api/reference-items/${itemId}/note`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });

export const deleteItem = (itemId: number) =>
  apiFetch<{ deleted: number }>(`/api/reference-items/${itemId}`, { method: "DELETE" });

/** Все библиотеки списком — для выбора «куда положить». */
export const flatLibraries = (list: LibraryList | undefined): LibraryCard[] =>
  (list?.groups ?? []).flatMap((g) => g.libs);

/** «1 фото», «3 фото» — у слова нет форм, но у «проектов» есть. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

export const loadAllItems = (params: AllItemsParams) =>
  apiFetch<AllItems>(`/api/reference-items${query(params)}`);
export const loadBasket = () => apiFetch<BasketView>("/api/basket");

const send = <T>(path: string, body: unknown = {}) =>
  apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) });

export const basketAdd = (ids: number[]) =>
  send<{ added: number; already: number; total: number }>("/api/basket/add", { item_ids: ids });
export const basketRemove = (ids: number[]) =>
  send<{ removed: number; total: number }>("/api/basket/remove", { item_ids: ids });
export const basketClear = () => send<{ removed: number }>("/api/basket/clear");

export const updateLibrary = (id: number, body: NewLibrary) =>
  send<{ library: Library }>(`/api/libraries/${id}/update`, body);
export const deleteLibrary = (id: number) =>
  apiFetch<{ deleted: number; items: number }>(`/api/libraries/${id}`, { method: "DELETE" });

/** `null` снимает вердикт. */
export const setVerdict = (ids: number[], verdict: Verdict | null) =>
  send<{ updated: number }>("/api/reference-items/verdict", { item_ids: ids, verdict: verdict ?? "" });
export const tagItems = (ids: number[], by: TagBy) =>
  send<{ tag: TagLite; added: number; already: number }>("/api/reference-items/tag", {
    item_ids: ids,
    tag_id: by.tagId ?? null,
    name: by.name ?? "",
  });
export const untagItems = (ids: number[], tagId: number) =>
  send<{ removed: number }>("/api/reference-items/untag", { item_ids: ids, tag_id: tagId });
export const moveItems = (ids: number[], libraryId: number) =>
  send<{ moved: number; already: number }>("/api/reference-items/move", { item_ids: ids, library_id: libraryId });
export const copyItems = (ids: number[], libraryId: number) =>
  send<{ copied: number; already: number }>("/api/reference-items/copy", { item_ids: ids, library_id: libraryId });
export const deleteItems = (ids: number[]) =>
  send<{ deleted: number }>("/api/reference-items/delete", { item_ids: ids });

export const attachToProject = (pid: number, ids: number[]) =>
  send<{ references: Reference[]; attached: number; already: number }>(`/api/projects/${pid}/references`, {
    reference_item_ids: ids,
  });

/** Локальный патч кэша экрана: применить `fn` к затронутым референсам, без перезапроса. */
export function patchItems<T extends { items: ReferenceItem[] }>(
  data: T | undefined,
  ids: ReadonlySet<number>,
  fn: (i: ReferenceItem) => ReferenceItem,
): T | undefined {
  if (!data) return data;
  return { ...data, items: data.items.map((i) => (ids.has(i.id) ? fn(i) : i)) };
}
